/* SpawnAnything — WebAudio engine: one context (created/resumed on the first user gesture), buses
   master -> compressor -> out with music / sfx / ambience sub-buses, persisted volume + mute settings,
   lazily rendered synth buffers (see synth.js) and a voice limiter. */
import { SOUNDS, renderSound, soundRate } from './synth.js';
import { VoiceLimiter } from './mixer.js';
import { SAMPLE_SETS, sampleFiles } from './samples.js';

const KEY = 'aw-audio';
const DEFAULTS = { master: 0.8, music: 0.45, sfx: 0.9, muted: false };

export function createAudioEngine() {
  const settings = { ...DEFAULTS };
  try { Object.assign(settings, JSON.parse(localStorage.getItem(KEY) || '{}')); } catch (e) { }
  for (const k of ['master', 'music', 'sfx']) settings[k] = Math.min(1, Math.max(0, +settings[k] || 0));
  settings.muted = !!settings.muted;
  const listeners = new Set();
  let ctx = null, out = null, buses = null, unlocked = false;
  const buffers = new Map(); // name#variant -> AudioBuffer
  const limiter = new VoiceLimiter(16, 0.45); // battle voices get 7 slots, explosions/UI the rest
  const onUnlock = [];
  const stats = {}; // plays per sound (debugging: __awAudio.stats in dev builds)

  function save() { try { localStorage.setItem(KEY, JSON.stringify(settings)); } catch (e) { } }
  function applyGains(tc = 0.05) {
    if (!ctx) return;
    const t = ctx.currentTime;
    buses.master.gain.setTargetAtTime(settings.muted ? 0 : settings.master, t, tc);
    buses.music.gain.setTargetAtTime(settings.music * 0.55, t, tc);
    buses.sfx.gain.setTargetAtTime(settings.sfx * 0.6, t, tc); // headroom: many voices sum up
  }
  function init() {
    if (ctx) return true;
    const AC = globalThis.AudioContext || globalThis.webkitAudioContext;
    if (!AC) return false;
    try { ctx = new AC({ latencyHint: 'interactive' }); } catch (e) { ctx = null; return false; }
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -22; comp.knee.value = 12; comp.ratio.value = 8; comp.attack.value = 0.003; comp.release.value = 0.3;
    const master = ctx.createGain(), music = ctx.createGain(), sfx = ctx.createGain(), amb = ctx.createGain();
    music.connect(master); amb.connect(sfx); sfx.connect(comp); comp.connect(master); master.connect(ctx.destination);
    buses = { master, music, sfx, amb, comp };
    out = master;
    applyGains(0.001);
    return true;
  }
  function unlock() {
    if (!init()) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => { });
    if (!unlocked) { unlocked = true; for (const f of onUnlock.splice(0)) f(); prewarm(); loadSamples(); }
  }
  // first gesture unlocks audio (autoplay policy); keep listening because Safari may suspend again
  for (const ev of ['pointerdown', 'keydown', 'touchend']) addEventListener(ev, unlock, { capture: true, passive: true });

  /** AudioBuffer for a sound (rendered on first use) */
  function buffer(name, variant = 0) {
    const S = SOUNDS[name]; if (!S || !ctx) return null;
    const v = variant % (S.variants || 1), key = name + '#' + v;
    let b = buffers.get(key);
    if (!b) {
      const sr = soundRate(name), data = renderSound(name, sr, v);
      b = ctx.createBuffer(1, data.length, sr); b.copyToChannel(data, 0);
      buffers.set(key, b);
    }
    return b;
  }
  // recorded sounds (src/audio/samples.js): fetched + decoded once after unlock; until then the synth voice plays
  const recorded = new Map(); // file -> AudioBuffer
  function loadSamples() {
    const base = (import.meta.env && import.meta.env.BASE_URL) || '/';
    for (const f of sampleFiles()) {
      fetch(base + 'sfx/' + f).then(r => r.ok ? r.arrayBuffer() : Promise.reject(r.status))
        .then(a => new Promise((res, rej) => ctx.decodeAudioData(a, res, rej))).then(b => recorded.set(f, b))
        .catch(() => { /* keep the synth voice for this one */ });
    }
  }
  function sampleFor(name, variant) {
    const S = SAMPLE_SETS[name]; if (!S || !recorded.size) return null;
    for (let k = 0; k < S.n; k++) { const b = recorded.get(`${S.set}_${(variant + k) % S.n}.m4a`); if (b) return { b, rate: S.rate || 1, gain: S.gain ?? 1 }; }
    return null;
  }
  // render every buffer in small idle slices right after unlock, so nothing renders mid-battle
  function prewarm() {
    const todo = [];
    for (const [k, S] of Object.entries(SOUNDS)) for (let v = 0; v < (S.variants || 1); v++) todo.push([k, v]);
    const idle = globalThis.requestIdleCallback || (f => setTimeout(() => f({ timeRemaining: () => 8 }), 30));
    const step = dl => { let n = 0; while (todo.length && (n++ === 0 || dl.timeRemaining() > 6)) { const [k, v] = todo.shift(); buffer(k, v); } if (todo.length) idle(step); };
    idle(step);
  }

  /**
   * Play a one-shot. o: { gain, pan, rate, prio (0 battle / 1 important), bus ('sfx'|'music'|'amb'), when (ctx time), variant }
   * Returns the source node or null (muted, locked, or voice limit hit).
   */
  function play(name, o = {}) {
    if (!ctx || !unlocked || ctx.state !== 'running' || settings.muted) return null;
    const S = SOUNDS[name]; if (!S) return null;
    const rate = o.rate || 1, gain = o.gain == null ? 1 : o.gain, bus = o.bus || 'sfx';
    if (gain < 0.004) return null;
    const when = o.when || ctx.currentTime;
    const variant = o.variant == null ? (Math.random() * 8) | 0 : o.variant, rec = sampleFor(name, variant);
    const r = rate * (rec ? rec.rate : 1);
    if (bus !== 'music' && !limiter.acquire(ctx.currentTime, (rec ? rec.b.duration : S.dur) / r, o.prio || 0)) return null;
    const b = rec ? rec.b : buffer(name, variant); if (!b) return null;
    const src = ctx.createBufferSource(); src.buffer = b; src.playbackRate.value = r;
    const g = ctx.createGain(); g.gain.value = gain * (rec ? rec.gain : 1);
    let node = src.connect(g);
    if (o.pan && ctx.createStereoPanner) { const p = ctx.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, o.pan)); node = node.connect(p); }
    node.connect(buses[bus] || buses.sfx);
    src.start(when);
    stats[name] = (stats[name] || 0) + 1;
    return src;
  }
  /** looping bed; returns a handle with setGain/setRate (smoothed) and stop */
  function loop(name, bus = 'amb') {
    if (!ctx) return null;
    const b = buffer(name); if (!b) return null;
    const src = ctx.createBufferSource(); src.buffer = b; src.loop = true;
    const g = ctx.createGain(); g.gain.value = 0;
    src.connect(g).connect(buses[bus] || buses.sfx); src.start();
    return {
      setGain(v, tc = 0.4) { g.gain.setTargetAtTime(v, ctx.currentTime, tc); },
      setRate(r, tc = 0.2) { src.playbackRate.setTargetAtTime(r, ctx.currentTime, tc); },
      stop() { try { src.stop(); } catch (e) { } },
    };
  }

  const api = {
    settings,
    get ctx() { return ctx; },
    get ready() { return !!ctx && unlocked && ctx.state === 'running'; },
    get buses() { return buses; },
    buffer, play, loop, unlock,
    /** run f once audio is unlocked (immediately if it already is) */
    whenReady(f) { if (unlocked) f(); else onUnlock.push(f); },
    now() { return ctx ? ctx.currentTime : 0; },
    set(k, v) {
      if (!(k in DEFAULTS)) return;
      settings[k] = k === 'muted' ? !!v : Math.min(1, Math.max(0, +v || 0));
      save(); applyGains(); for (const f of listeners) f(settings);
    },
    toggleMute() { api.set('muted', !settings.muted); return settings.muted; },
    onChange(f) { listeners.add(f); return () => listeners.delete(f); },
    /** short UI blip that ignores the battle voice budget */
    ui(name = 'click', gain = 0.35) { return play(name, { gain, prio: 1, variant: 0 }); },
    stats,
  };
  if (import.meta.env && import.meta.env.DEV) globalThis.__awAudio = api;
  return api;
}
